import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TacoAssetType, CANONICAL_ASSET_PATHS } from "@amzn/afxtaco-types";
import { preprocessAssetContent } from "@/utils/markdown";
import DiagramImage from "./diagram/DiagramImage";

/** Canonical project-relative path of the rendered architecture diagram SVG. */
const DIAGRAM_SVG_PATH =
  CANONICAL_ASSET_PATHS[TacoAssetType.ARCHITECTURE_DIAGRAM_SVG];

interface MarkdownRendererProps {
  content: string;
  /**
   * When provided, markdown image references to the canonical diagram path
   * (`diagrams/diagram.svg`) are resolved to the project's rendered SVG via a
   * presigned URL. Without it, images render as plain `<img>` (no-op resolution).
   */
  projectId?: string;
}

/** True when an <img> src points at the canonical diagram SVG path. */
function isDiagramSvgSrc(src: string | undefined): boolean {
  if (!src) return false;
  // Match the project-relative canonical path, tolerating a leading ./ or /.
  const normalized = src.replace(/^\.?\//, "");
  return normalized === DIAGRAM_SVG_PATH;
}

/**
 * Reusable markdown renderer with consistent styling for project assets
 * (summary, implementation, thinking tabs).
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  projectId,
}) => {
  const processedContent = preprocessAssetContent(content);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 className="text-2xl font-semibold mb-4 text-foreground">
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-xl font-semibold mb-3 mt-6 text-foreground">
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-medium mb-2 mt-4 text-foreground">
            {children}
          </h3>
        ),
        h4: ({ children }) => (
          <h4 className="text-base font-medium mb-2 mt-3 text-foreground">
            {children}
          </h4>
        ),
        p: ({ children }) => (
          <p className="text-sm mb-3 last:mb-0 text-foreground leading-relaxed">
            {children}
          </p>
        ),
        ul: ({ children }) => (
          <ul className="mb-3 ml-4 list-disc space-y-1">{children}</ul>
        ),
        ol: ({ children, start }) => (
          <ol start={start} className="mb-3 ml-4 list-decimal space-y-1">
            {children}
          </ol>
        ),
        li: ({ children }) => (
          <li className="text-sm text-foreground">{children}</li>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold text-foreground">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children, className }) => {
          // Fenced code blocks get a className like "language-json"
          const isBlock = typeof className === "string";
          if (isBlock) {
            return (
              <code className="block bg-muted p-4 rounded-lg text-xs font-mono overflow-x-auto mb-3">
                {children}
              </code>
            );
          }
          return (
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre className="mb-3 overflow-x-auto">{children}</pre>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic text-muted-foreground mb-3">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-6 border-border" />,
        table: ({ children }) => (
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-sm border-collapse border border-border rounded-lg">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
        th: ({ children }) => (
          <th className="border border-border px-3 py-2 text-left font-medium text-foreground">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-3 py-2 text-foreground">
            {children}
          </td>
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            className="text-blue-600 dark:text-blue-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
        img: ({ src, alt }) => {
          // Resolve references to the canonical rendered diagram SVG to the
          // project's presigned URL (triggers on-demand render server-side).
          if (
            projectId &&
            isDiagramSvgSrc(typeof src === "string" ? src : undefined)
          ) {
            return <DiagramImage projectId={projectId} alt={alt} />;
          }
          // Other images: render as-is (only if they have a usable src).
          if (typeof src === "string" && src.length > 0) {
            return (
              <img src={src} alt={alt ?? ""} className="max-w-full h-auto" />
            );
          }
          return null;
        },
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
